<template>
  <controller :goToTopButton="true">
    <page-header title="Copiloto de chamados" class="copilot-apollo-header">
      <template #side>
        <button type="button" class="button secondary" :disabled="syncing" @click="syncTickets">
          <fa :icon="['fal', 'rotate']" :spin="syncing" />
          {{ syncing ? 'Sincronizando' : 'Buscar chamados' }}
        </button>
      </template>
      <p class="apollo-header-description"><strong>Engenharia · Agenda Edu</strong><span>O Codex investiga, aprende com a orientação do time e só executa ou responde após aprovação.</span></p>
    </page-header>

    <main class="copilot-page">
      <section class="stats">
        <button type="button" :class="{ active: tab === 'tickets' && !statusFilter }" @click="openTickets()"><strong>{{ overview.open || 0 }}</strong><span>chamados em aberto</span></button>
        <button type="button" :class="{ active: statusFilter === 'needs_guidance' }" @click="openTickets('needs_guidance')"><strong>{{ overview.needs_guidance || 0 }}</strong><span>precisam de orientação</span></button>
        <button type="button" :class="{ active: statusFilter === 'awaiting' }" @click="openTickets('awaiting')"><strong>{{ overview.awaiting_approval || 0 }}</strong><span>aguardando sua aprovação</span></button>
        <button type="button" :class="{ active: tab === 'resolved' }" @click="openResolved"><strong>{{ overview.resolved || 0 }}</strong><span>resolvidos pelo fluxo</span></button>
      </section>

      <nav class="tabs" aria-label="Áreas do Copiloto">
        <button type="button" :class="{ active: tab === 'tickets' }" @click="openTickets()">Em andamento</button>
        <button type="button" :class="{ active: tab === 'resolved' }" @click="openResolved">Resolvidos <span class="tab-count">{{ overview.resolved || 0 }}</span></button>
        <button type="button" :class="{ active: tab === 'knowledge' }" @click="openKnowledge">Base de conhecimento <span v-if="overview.knowledge_candidates" class="tab-count attention">{{ overview.knowledge_candidates }}</span></button>
      </nav>

      <section v-if="tab === 'tickets' && showGuide" class="quick-guide">
        <div>
          <span class="eyebrow">Primeiros passos</span>
          <h2>O Copiloto prepara; você decide</h2>
          <p>Comece pelo chamado, oriente quando faltar contexto e aprove somente depois de revisar.</p>
        </div>
        <ol>
          <li><strong>1</strong><span><b>Escolha o chamado</b>Leia a conversa completa.</span></li>
          <li><strong>2</strong><span><b>Informe o projeto</b>Ajuda o Codex a procurar no lugar certo.</span></li>
          <li><strong>3</strong><span><b>Revise a análise</b>Oriente ou aprove a solução proposta.</span></li>
          <li><strong>4</strong><span><b>Aprove a resposta</b>O envio e o encerramento só acontecem com sua confirmação.</span></li>
        </ol>
        <button type="button" class="guide-close" @click="showGuide = false">Entendi</button>
      </section>

      <section v-if="['tickets', 'resolved'].includes(tab)" class="workspace" :class="{ 'has-detail': detail || detailLoading }">
        <aside class="ticket-panel">
          <div class="ticket-toolbar">
            <label class="search-box">
              <fa :icon="['fal', 'magnifying-glass']" />
              <span class="sr-only">Buscar ticket ou solicitante</span>
              <input v-model="search" placeholder="Buscar ticket ou solicitante" @keyup.enter="loadTickets" />
            </label>
            <div v-if="tab === 'tickets'" class="filters" aria-label="Filtrar chamados">
              <button type="button" :class="{ active: !statusFilter }" @click="setStatusFilter('')">Todos</button>
              <button type="button" :class="{ active: statusFilter === 'needs_guidance' }" @click="setStatusFilter('needs_guidance')">Orientar</button>
              <button type="button" :class="{ active: statusFilter === 'awaiting' }" @click="setStatusFilter('awaiting')">Aprovar</button>
              <button type="button" :class="{ active: statusFilter === 'processing' }" @click="setStatusFilter('processing')">Processando</button>
            </div>
          </div>
          <div class="ticket-list">
            <div v-if="loading" class="empty loading-state"><span class="pulse"></span> Carregando chamados…</div>
            <template v-else>
              <button
                v-for="ticket in tickets"
                :key="ticket.id"
                type="button"
                class="ticket-card"
                :class="{ selected: selected?.id === ticket.id }"
                @click="selectTicket(ticket)"
              >
                <div class="ticket-line">
                  <span>#{{ ticket.agenda_ticket_id }}</span>
                  <small>{{ date(ticket.last_message_at) }}</small>
                </div>
                <strong>{{ ticket.requester_name || 'Solicitante' }}</strong>
                <p>{{ ticket.subject || latestPreview(ticket) }}</p>
                <div class="ticket-meta">
                  <span class="status" :class="ticket.automation_status">{{ statusLabel(ticket.automation_status) }}</span>
                  <span class="project-name">{{ ticket.project?.name || 'Projeto não definido' }}</span>
                </div>
              </button>
              <div v-if="!tickets.length" class="empty">{{ tab === 'resolved' ? 'Nenhum chamado foi resolvido pelo fluxo ainda.' : 'Nenhum chamado encontrado neste filtro.' }}</div>
            </template>
          </div>
        </aside>

        <article v-if="detailLoading" class="detail-panel detail-loading" aria-live="polite">
          <span class="pulse"></span>
          <strong>Carregando o chamado…</strong>
          <p>Buscando conversa, análise e histórico.</p>
        </article>

        <article v-else-if="detail" class="detail-panel">
          <header class="detail-header">
            <div>
              <span class="eyebrow">Ticket #{{ detail.agenda_ticket_id }}</span>
              <h2>{{ detail.subject || 'Chamado de Engenharia' }}</h2>
              <p>{{ detail.requester_name }} · {{ remoteStatus(detail.status) }} · {{ detail.project?.name || 'Projeto ainda não definido' }}</p>
            </div>
            <label class="project-select">
              <span>Projeto relacionado</span>
              <select v-model="selectedProjectId" :disabled="isResolved">
                <option :value="null">Ainda não identificado</option>
                <option v-for="project in projects" :key="project.id" :value="project.id">{{ project.name }}</option>
              </select>
            </label>
          </header>

          <section class="next-step" :class="nextStep.tone">
            <div>
              <span>Próximo passo</span>
              <h3>{{ nextStep.title }}</h3>
              <p>{{ nextStep.description }}</p>
            </div>
            <button v-if="nextStep.action === 'knowledge'" type="button" class="button secondary small" @click="openKnowledge">Revisar aprendizado</button>
          </section>

          <section v-if="isResolved" class="resolution-summary">
            <div class="resolution-heading">
              <div><span class="eyebrow">Resultado final</span><h3>Chamado resolvido pelo fluxo</h3></div>
              <span class="status published">Concluído</span>
            </div>
            <div class="resolution-grid">
              <div><span>Resposta ao usuário</span><p>{{ publicationRun?.options?.send_reply ? 'Enviada e confirmada na Agenda Edu' : 'Não solicitada neste fluxo' }}</p></div>
              <div><span>Encerramento</span><p>{{ publicationRun?.options?.close_ticket ? 'Ticket encerrado na Agenda Edu' : 'Mantido aberto por escolha do aprovador' }}</p></div>
              <div><span>Alteração técnica</span><p>{{ publicationRun?.branch_name || (publicationRun?.options?.push_branch ? 'Publicação solicitada' : 'Somente orientação ou resposta') }}</p></div>
              <div><span>Aprendizado</span><p>{{ detail.knowledge_entries?.length ? `${detail.knowledge_entries.length} registro(s) aguardando ou já revisado(s)` : 'Será criado automaticamente para revisão' }}</p></div>
            </div>
          </section>

          <section class="conversation">
            <div class="conversation-heading">
              <div>
                <h3>Conversa completa</h3>
                <p>{{ detail.messages?.length || 0 }} mensagem(ns) sincronizada(s) da Agenda Edu.</p>
              </div>
              <button type="button" class="text-button" :aria-expanded="showConversation" @click="showConversation = !showConversation">
                {{ showConversation ? 'Ocultar conversa' : 'Ver conversa' }}
              </button>
            </div>
            <div v-if="showConversation" class="message-list">
              <div
                v-for="message in detail.messages"
                :key="message.id"
                class="message"
                :class="message.direction"
              >
                <div>
                  <strong>{{ message.sender_name || (message.direction === 'outgoing' ? 'Equipe' : 'Solicitante') }}</strong>
                  <time>{{ dateTime(message.remote_created_at) }}</time>
                </div>
                <p>{{ message.body || '[anexo ou evento sem texto]' }}</p>
              </div>
            </div>
          </section>

          <section class="codex-card">
            <div class="codex-title">
              <div class="codex-icon"><fa :icon="['fal', 'sparkles']" /></div>
              <div>
                <span class="eyebrow">Codex</span>
                <h3>{{ latestRun ? runTitle(latestRun) : 'Pronto para investigar' }}</h3>
              </div>
              <span v-if="latestRun" class="status" :class="latestRun.status">{{ statusLabel(latestRun.status) }}</span>
            </div>

            <template v-if="runResult">
              <div class="analysis-grid">
                <section class="analysis-section summary-copy">
                  <span>Resumo executivo</span>
                  <div class="formatted-copy">
                    <p v-for="(paragraph, index) in readableParagraphs(runResult.summary, 2)" :key="`summary-${index}`">{{ paragraph }}</p>
                  </div>
                </section>
                <section class="analysis-section diagnosis-copy">
                  <span>Diagnóstico</span>
                  <div class="formatted-copy">
                    <p v-for="(paragraph, index) in readableParagraphs(runResult.diagnosis, 2)" :key="`diagnosis-${index}`">{{ paragraph }}</p>
                  </div>
                </section>
              </div>
              <div v-if="runResult.evidence?.length" class="evidence-list">
                <strong>Evidências verificadas pelo Codex</strong>
                <ul>
                  <li v-for="(evidence, index) in runResult.evidence" :key="`${evidence.source}-${index}`">
                    <div><b>{{ evidence.source }}</b><code v-if="evidence.reference">{{ evidence.reference }}</code></div>
                    <div class="formatted-copy evidence-copy"><p v-for="(paragraph, paragraphIndex) in readableParagraphs(evidence.finding, 2)" :key="paragraphIndex">{{ paragraph }}</p></div>
                  </li>
                </ul>
              </div>
              <div v-if="runResult.diagnostics_used?.length" class="diagnostic-tools">
                <span>Investigação automática</span>
                <b>{{ runResult.diagnostics_used.length }} consulta(s) em infraestrutura, logs ou dados</b>
              </div>
              <div v-if="runResult.questions?.length" class="questions">
                <strong>Bloqueio que não foi possível descobrir automaticamente</strong>
                <ol><li v-for="question in runResult.questions" :key="question">{{ question }}</li></ol>
              </div>
              <div v-if="runResult.proposed_plan?.length" class="plan">
                <strong>Plano proposto</strong>
                <ol><li v-for="step in runResult.proposed_plan" :key="step">{{ step }}</li></ol>
              </div>
              <div v-if="runResult.files_changed?.length" class="files">
                <strong>Arquivos alterados</strong>
                <code v-for="file in runResult.files_changed" :key="file">{{ file }}</code>
              </div>
            </template>

            <div v-if="latestRun?.error_message" class="error-box">{{ latestRun.error_message }}</div>

            <div v-if="!latestRun" class="actions">
              <button type="button" class="button primary" :disabled="acting" @click="analyze">Analisar chamado</button>
            </div>
            <div v-else-if="['needs_guidance', 'ready_for_execution', 'rejected'].includes(latestRun.status)" class="approval-block">
              <textarea v-model="guidance" placeholder="Informe somente uma regra de negócio, decisão ou acesso que o Codex realmente não conseguiu descobrir…" />
              <div class="actions">
                <button class="button secondary" :disabled="acting || !guidance.trim()" @click="sendGuidance">Orientar e analisar novamente</button>
                <button v-if="latestRun.status === 'ready_for_execution'" class="button secondary" :disabled="acting" @click="prepareResponse">Preparar somente resposta</button>
                <button v-if="latestRun.status === 'ready_for_execution'" class="button primary" :disabled="acting || !selectedProjectId" @click="approveExecution">Aprovar implementação</button>
              </div>
            </div>
            <div v-else-if="latestRun.status === 'awaiting_approval'" class="publish-block">
              <label>Mensagem sugerida<textarea v-model="replyMessage" /></label>
              <div class="checks">
                <label><input v-model="publishOptions.push_branch" type="checkbox" :disabled="latestRun.action === 'prepare_response'" /> Publicar branch no GitHub</label>
                <label><input v-model="publishOptions.send_reply" type="checkbox" /> Iniciar se necessário e responder na Agenda Edu</label>
                <label><input v-model="publishOptions.close_ticket" type="checkbox" /> Encerrar após confirmar a resposta</label>
              </div>
              <label>Correção ou motivo da rejeição<textarea v-model="rejection" placeholder="Preencha somente se for devolver ao Codex" /></label>
              <div class="actions">
                <button class="button danger" :disabled="acting || !rejection.trim()" @click="rejectRun">Devolver com orientação</button>
                <button class="button primary" :disabled="acting || (publishOptions.send_reply && !replyMessage.trim())" @click="approvePublication">Aprovar publicação</button>
              </div>
            </div>
            <div v-else-if="['queued', 'running'].includes(latestRun.status)" class="working">
              <div><span class="pulse"></span> O worker está processando esta etapa. A tela será atualizada silenciosamente.</div>
              <small>O processamento tem limite de 10 minutos e será liberado para nova tentativa se falhar.</small>
            </div>
            <div v-else-if="latestRun.status === 'failed'" class="actions">
              <button v-if="implementationForPublication" class="button primary" :disabled="acting" @click="approvePublication">Tentar publicação novamente</button>
              <button v-else class="button primary" :disabled="acting" @click="analyze">Analisar novamente</button>
            </div>
          </section>

          <section v-if="detail.runs?.length" class="workflow-history">
            <div class="section-heading">
              <div><h3>Histórico do atendimento</h3><p>Veja o que o Copiloto fez e quais decisões foram tomadas.</p></div>
            </div>
            <ol>
              <li v-for="run in detail.runs" :key="run.id">
                <span class="timeline-dot" :class="run.status"></span>
                <div class="timeline-content">
                  <div><strong>{{ runTitle(run) }}</strong><span class="status" :class="run.status">{{ statusLabel(run.status) }}</span><time>{{ dateTime(run.finished_at || run.updated_at || run.created_at) }}</time></div>
                  <p v-if="run.result?.summary">{{ run.result.summary }}</p>
                  <p v-else-if="run.action === 'publish' && run.status === 'published'">Resposta e encerramento executados conforme a aprovação.</p>
                  <p v-else-if="run.error_message" class="timeline-error">{{ run.error_message }}</p>
                  <small v-if="run.branch_name">Branch: {{ run.branch_name }}</small>
                </div>
              </li>
            </ol>
          </section>
        </article>

        <article v-else class="detail-panel empty-state">
          <fa :icon="['fal', 'comment']" />
          <h2>Selecione um chamado</h2>
          <p>A conversa, a análise e as aprovações aparecerão aqui.</p>
        </article>
      </section>

      <section v-else class="knowledge-panel">
        <header class="knowledge-header">
          <div><span class="eyebrow">Memória da equipe</span><h2>Base de conhecimento</h2><p>Cada resolução gera um candidato. Depois da revisão, ele passa a orientar automaticamente casos parecidos.</p></div>
          <div class="knowledge-summary"><strong>{{ overview.knowledge_candidates || 0 }}</strong><span>para revisar</span><strong>{{ overview.knowledge_approved || 0 }}</strong><span>aprovados</span></div>
        </header>
        <div class="knowledge-toolbar">
          <div class="search-box"><fa :icon="['fal', 'magnifying-glass']" /><input v-model="knowledgeSearch" placeholder="Buscar sintoma ou solução" @keyup.enter="openKnowledge" /></div>
          <div class="filters">
            <button :class="{ active: !knowledgeStatus }" @click="setKnowledgeFilter('')">Todos</button>
            <button :class="{ active: knowledgeStatus === 'candidate' }" @click="setKnowledgeFilter('candidate')">Para revisar</button>
            <button :class="{ active: knowledgeStatus === 'approved' }" @click="setKnowledgeFilter('approved')">Aprovados</button>
          </div>
        </div>
        <article v-for="entry in knowledge" :key="entry.id" class="knowledge-card">
          <div class="knowledge-meta"><span class="status" :class="entry.status">{{ statusLabel(entry.status) }}</span><small>confiança {{ Math.round(entry.confidence * 100) }}%</small><small v-if="entry.project">{{ entry.project.name }}</small></div>
          <h3>{{ entry.title }}</h3>
          <p><strong>Sintoma:</strong> {{ entry.symptom }}</p>
          <p><strong>Solução:</strong> {{ entry.resolution }}</p>
          <p v-if="entry.validation"><strong>Como foi validado:</strong> {{ entry.validation }}</p>
          <div v-if="entry.tags?.length" class="tags"><span v-for="tag in entry.tags" :key="tag">{{ tag }}</span></div>
          <footer>
            <button v-if="entry.source_ticket" class="text-button" @click="openSourceTicket(entry.source_ticket)">Ver ticket #{{ entry.source_ticket.agenda_ticket_id }}</button>
            <button v-if="entry.status === 'candidate'" class="button primary small" @click="approveKnowledge(entry)">Revisado: aprovar conhecimento</button>
          </footer>
        </article>
        <div v-if="!knowledge.length" class="empty knowledge-empty"><h3>Nenhum aprendizado neste filtro</h3><p>Ao concluir um chamado, a solução aparece aqui como candidata para revisão. Conhecimentos aprovados são enviados ao Codex nos próximos casos relacionados.</p><button class="button secondary small" @click="openResolved">Ver chamados resolvidos</button></div>
      </section>

      <div v-if="toast" class="toast" :class="toast.type">{{ toast.message }}</div>
    </main>
  </controller>
</template>

<script>
import EngineeringCopilotService from '@/services/engineering-copilot.service'

export default {
  data () {
    return {
      tab: 'tickets', loading: false, detailLoading: false, syncing: false, acting: false, search: '',
      tickets: [], projects: [], counts: {}, overview: {}, selected: null, detail: null, audit: [],
      selectedProjectId: null, guidance: '', rejection: '', replyMessage: '',
      knowledge: [], toast: null, refreshTimer: null, pollingDetail: false,
      statusFilter: '', knowledgeStatus: '', knowledgeSearch: '', showGuide: true, showConversation: false,
      publishOptions: { push_branch: true, send_reply: true, close_ticket: true },
    }
  },
  computed: {
    latestRun () { return this.detail?.runs?.[0] || null },
    analysisRun () { return this.detail?.runs?.find(run => run.action === 'analyze' && run.result) || null },
    publicationRun () { return this.detail?.runs?.find(run => run.action === 'publish' && run.status === 'published') || null },
    runResult () {
      const latestResult = this.latestRun?.result
      return latestResult?.summary || latestResult?.diagnosis ? latestResult : this.analysisRun?.result || latestResult || null
    },
    isResolved () { return Boolean(this.detail && (this.detail.automation_status === 'published' || this.detail.closed_at || !['waiting', 'in_attendance'].includes(this.detail.status))) },
    implementationForPublication () { return this.detail?.runs?.find(run => ['implement', 'prepare_response'].includes(run.action) && run.status === 'awaiting_approval') || null },
    nextStep () {
      if (this.isResolved) return { title: 'Revise o aprendizado gerado', description: 'A resolução ficou no histórico e deve virar referência após sua revisão.', tone: 'success', action: 'knowledge' }
      if (!this.latestRun) return { title: 'Escolha o projeto e peça uma análise', description: 'O Codex lerá toda a conversa e indicará diagnóstico, dúvidas e próximos passos.', tone: 'neutral', action: 'analyze' }
      if (['queued', 'running'].includes(this.latestRun.status)) return { title: 'Aguarde a análise', description: 'Você pode continuar navegando; esta tela será atualizada sem interromper seu trabalho.', tone: 'processing' }
      if (['needs_guidance', 'rejected'].includes(this.latestRun.status)) return { title: 'Oriente o Codex', description: 'Responda às dúvidas ou explique qual sistema e regra de negócio devem ser considerados.', tone: 'attention' }
      if (this.latestRun.status === 'ready_for_execution') return { title: 'Escolha como resolver', description: 'Você pode aprovar a implementação ou preparar somente uma resposta ao solicitante.', tone: 'attention' }
      if (this.latestRun.status === 'awaiting_approval') return { title: 'Revise e aprove a publicação', description: 'Confira a mensagem, escolha se deseja responder e encerrar, e então confirme.', tone: 'attention' }
      if (this.latestRun.status === 'failed') return { title: 'Tente novamente', description: 'A execução falhou sem publicar nada. Revise o erro e inicie uma nova análise.', tone: 'error' }
      return { title: 'Acompanhe o histórico abaixo', description: 'Todas as etapas e decisões deste atendimento ficam registradas.', tone: 'neutral' }
    },
  },
  mounted () {
    this.loadTickets()
    this.refreshTimer = setInterval(() => {
      if (!document.hidden && !this.pollingDetail && this.latestRun && ['queued', 'running'].includes(this.latestRun.status)) {
        this.reloadSelected({ background: true })
      }
    }, 12000)
  },
  beforeUnmount () { clearInterval(this.refreshTimer) },
  methods: {
    async loadTickets ({ silent = false } = {}) {
      if (!silent) this.loading = true
      try {
        const response = await EngineeringCopilotService.tickets({
          search: this.search || undefined,
          scope: this.tab === 'resolved' ? 'resolved' : 'open',
          automation_status: this.automationFilter(this.statusFilter),
        })
        this.tickets = response.data.data.data || []
        this.projects = response.data.projects || []
        this.counts = response.data.counts || {}
        this.overview = response.data.overview || this.overview
        if (this.selected) this.selected = this.tickets.find(ticket => ticket.id === this.selected.id) || this.selected
      } catch (error) { this.notifyError(error) }
      finally { if (!silent) this.loading = false }
    },
    async selectTicket (ticket) {
      this.selected = ticket
      this.detail = null
      this.detailLoading = true
      this.showConversation = false
      this.guidance = ''
      this.rejection = ''
      this.replyMessage = ''
      try { await this.reloadSelected() }
      finally { this.detailLoading = false }
    },
    async reloadSelected ({ background = false } = {}) {
      if (!this.selected) return
      if (background) this.pollingDetail = true
      try {
        const previousRunId = this.latestRun?.id
        const previousRunStatus = this.latestRun?.status
        const response = await EngineeringCopilotService.ticket(this.selected.id)
        this.detail = response.data.data
        this.audit = response.data.audit || []
        const runChanged = previousRunId !== this.latestRun?.id || previousRunStatus !== this.latestRun?.status
        if (!background || runChanged) {
          this.selectedProjectId = this.detail.project_id
          this.replyMessage = this.latestRun?.result?.draft_reply || this.replyMessage
          this.publishOptions.push_branch = this.latestRun?.action !== 'prepare_response'
        }
        await this.loadTickets({ silent: background })
      } catch (error) { this.notifyError(error) }
      finally { if (background) this.pollingDetail = false }
    },
    async syncTickets () {
      this.syncing = true
      try {
        const response = await EngineeringCopilotService.sync()
        await this.loadTickets()
        this.notify(`Sincronização concluída: ${response.data.data.synced} chamado(s).`)
      } catch (error) { this.notifyError(error) }
      finally { this.syncing = false }
    },
    async analyze () { await this.act(() => EngineeringCopilotService.analyze(this.detail.id, this.selectedProjectId), 'Análise adicionada à fila.') },
    async sendGuidance () {
      await this.act(() => EngineeringCopilotService.guidance(this.detail.id, this.guidance, this.selectedProjectId), 'Orientação salva e nova análise enfileirada.')
      this.guidance = ''
    },
    async approveExecution () { await this.act(() => EngineeringCopilotService.approveExecution(this.latestRun.id), 'Implementação aprovada e enfileirada.') },
    async prepareResponse () { await this.act(() => EngineeringCopilotService.prepareResponse(this.latestRun.id), 'Resposta preparada para sua revisão.') },
    async approvePublication () {
      const run = this.latestRun.status === 'failed' ? this.implementationForPublication : this.latestRun
      await this.act(() => EngineeringCopilotService.approvePublication(run.id, { ...this.publishOptions, reply_message: this.replyMessage }), 'Publicação aprovada.')
    },
    async rejectRun () {
      await this.act(() => EngineeringCopilotService.reject(this.latestRun.id, this.rejection), 'Execução devolvida para orientação.')
      this.guidance = this.rejection
      this.rejection = ''
    },
    async act (callback, message) {
      this.acting = true
      try { await callback(); await this.reloadSelected(); this.notify(message) }
      catch (error) { this.notifyError(error) }
      finally { this.acting = false }
    },
    async openKnowledge () {
      this.tab = 'knowledge'
      this.selected = null
      this.detail = null
      this.detailLoading = false
      try {
        const response = await EngineeringCopilotService.knowledge({ status: this.knowledgeStatus || undefined, search: this.knowledgeSearch || undefined })
        this.knowledge = response.data.data.data || []
      } catch (error) { this.notifyError(error) }
    },
    async openTickets (filter = '') {
      this.tab = 'tickets'
      this.statusFilter = filter
      this.selected = null
      this.detail = null
      this.detailLoading = false
      await this.loadTickets()
    },
    async openResolved () {
      this.tab = 'resolved'
      this.statusFilter = ''
      this.selected = null
      this.detail = null
      this.detailLoading = false
      await this.loadTickets()
    },
    async openSourceTicket (ticket) {
      this.tab = 'resolved'
      this.statusFilter = ''
      await this.loadTickets()
      this.selected = this.tickets.find(item => item.id === ticket.id) || ticket
      await this.reloadSelected()
    },
    setStatusFilter (filter) { this.statusFilter = filter; this.selected = null; this.detail = null; this.loadTickets() },
    setKnowledgeFilter (filter) { this.knowledgeStatus = filter; this.openKnowledge() },
    async approveKnowledge (entry) {
      try {
        await EngineeringCopilotService.updateKnowledge(entry.id, { status: 'approved' })
        this.overview.knowledge_candidates = Math.max(0, Number(this.overview.knowledge_candidates || 0) - 1)
        this.overview.knowledge_approved = Number(this.overview.knowledge_approved || 0) + 1
        await this.openKnowledge()
        this.notify('Conhecimento aprovado e disponível para os próximos chamados.')
      }
      catch (error) { this.notifyError(error) }
    },
    count (status) { return Number(this.counts[status] || 0) },
    automationFilter (filter) {
      return ({
        awaiting: 'awaiting_execution_approval,awaiting_publication_approval',
        processing: 'queued_for_analysis,analyzing,queued_for_implementation,queued_for_publication',
      })[filter] || filter || undefined
    },
    readableParagraphs (value, sentencesPerParagraph = 2) {
      const normalized = String(value || '').replace(/\r\n/g, '\n').trim()
      if (!normalized) return []

      const explicitParagraphs = normalized
        .split(/\n{2,}/)
        .map(paragraph => paragraph.replace(/\n/g, ' ').trim())
        .filter(Boolean)
      if (explicitParagraphs.length > 1) return explicitParagraphs

      const explicitLines = normalized.split(/\n+/).map(line => line.trim()).filter(Boolean)
      if (explicitLines.length > 1) return explicitLines

      const sentences = normalized
        .replace(/([.!?]+)\s+(?=[A-ZÀ-ÖØ-Þ0-9])/g, '$1\n')
        .split('\n')
        .map(sentence => sentence.trim())
        .filter(Boolean)
      if (sentences.length <= sentencesPerParagraph) return [normalized]

      const paragraphs = []
      for (let index = 0; index < sentences.length; index += sentencesPerParagraph) {
        paragraphs.push(sentences.slice(index, index + sentencesPerParagraph).join(' '))
      }
      return paragraphs
    },
    latestPreview () { return 'Aguardando leitura da conversa' },
    statusLabel (status) {
      return ({
        new: 'Novo', queued_for_analysis: 'Análise na fila', analyzing: 'Analisando', needs_guidance: 'Precisa de orientação',
        awaiting_execution_approval: 'Aprovar execução', queued_for_implementation: 'Implementação na fila',
        awaiting_publication_approval: 'Aprovar resposta', queued_for_publication: 'Publicação na fila', published: 'Publicado',
        queued: 'Na fila', running: 'Em processamento', ready_for_execution: 'Plano pronto', awaiting_approval: 'Revisar solução',
        failed: 'Falhou', rejected: 'Devolvido', candidate: 'Para revisar', approved: 'Aprovado', deprecated: 'Obsoleto',
      })[status] || status
    },
    runTitle (run) { return ({ analyze: 'Análise do chamado', implement: 'Implementação da solução', prepare_response: 'Resposta sem alteração de código', publish: 'Publicação aprovada' })[run.action] || 'Execução Codex' },
    remoteStatus (status) { return ({ in_attendance: 'Em atendimento', waiting: 'Aguardando atendimento', done: 'Encerrado', closed: 'Encerrado', concluded: 'Encerrado' })[status] || status },
    date (value) { return value ? new Intl.DateTimeFormat('pt-BR', { day: '2-digit', month: 'short' }).format(new Date(value)) : '' },
    dateTime (value) { return value ? new Intl.DateTimeFormat('pt-BR', { dateStyle: 'short', timeStyle: 'short' }).format(new Date(value)) : '' },
    notify (message, type = 'success') { this.toast = { message, type }; setTimeout(() => { this.toast = null }, 4500) },
    notifyError (error) { this.notify(error.response?.data?.message || 'Não foi possível concluir a operação.', 'error') },
  },
}
</script>

<style lang="scss" scoped>
.copilot-apollo-header {
  .apollo-header-description { display: flex; flex-wrap: wrap; gap: 5px 12px; margin: 10px 0 0; color: #6d7184; font-size: 12px;
    strong { color: #d52747; font-size: 10px; letter-spacing: .08em; text-transform: uppercase; }
  }
}
.copilot-page { min-height: 100%; padding: 24px 28px 34px; background: #f6f7fb; color: #25283a; }
.eyebrow { color: #ec3151; font-size: 10px; font-weight: 800; letter-spacing: .09em; text-transform: uppercase; }
.sr-only { position: absolute; width: 1px; height: 1px; padding: 0; margin: -1px; overflow: hidden; clip: rect(0, 0, 0, 0); white-space: nowrap; border: 0; }
.button { border: 0; border-radius: 9px; padding: 11px 16px; font-weight: 700; display: inline-flex; gap: 8px; align-items: center; justify-content: center; cursor: pointer; transition: transform .18s, box-shadow .18s, border-color .18s;
  &:hover:not(:disabled) { transform: translateY(-1px); box-shadow: 0 5px 15px #25283a14; }
  &:focus-visible { outline: 3px solid #ed274c35; outline-offset: 2px; }
  &:disabled { opacity: .5; cursor: not-allowed; }
  &.primary { background: #ed274c; color: white; }
  &.secondary { background: white; color: #373b50; border: 1px solid #d9dce5; }
  &.danger { background: #fff1f3; color: #bd1f3a; }
  &.small { padding: 7px 11px; font-size: 12px; }
}
.stats { display: grid; grid-template-columns: repeat(4, minmax(0, 1fr)); gap: 12px; margin-bottom: 18px;
  button { appearance: none; min-height: 80px; text-align: left; background: white; border: 1px solid #e2e4eb; border-radius: 12px; padding: 14px 16px; display: flex; flex-direction: column; justify-content: center; cursor: pointer; transition: border-color .2s, box-shadow .2s, transform .2s;
    &:hover, &.active { border-color: #ef8da0; box-shadow: 0 6px 18px #6714260d; transform: translateY(-1px); }
    &:focus-visible { outline: 3px solid #ed274c2b; outline-offset: 2px; }
  }
  strong { font-size: 23px; line-height: 1.1; }
  span { margin-top: 4px; color: #777b8e; font-size: 11px; }
}
.tabs { display: flex; gap: 24px; border-bottom: 1px solid #dfe1e9; margin-bottom: 16px;
  button { border: 0; background: none; padding: 10px 2px 9px; color: #777b8e; font-weight: 700; border-bottom: 3px solid transparent; display: flex; gap: 7px; align-items: center; cursor: pointer; }
  button:hover { color: #43475a; }
  button.active { color: #d92345; border-color: #ed274c; }
  button:focus-visible { outline: 2px solid #ed274c45; outline-offset: 2px; border-radius: 4px; }
}
.tab-count { min-width: 20px; height: 20px; display: inline-grid; place-items: center; padding: 0 6px; border-radius: 999px; background: #eceef4; color: #616679; font-size: 10px;
  &.attention { background: #fff0d8; color: #9c6315; }
}
.quick-guide { position: relative; display: grid; grid-template-columns: minmax(220px, .8fr) 2fr; gap: 28px; background: linear-gradient(135deg, #fff, #fff7f8); border: 1px solid #f1dadd; border-radius: 13px; padding: 18px 54px 18px 20px; margin-bottom: 16px;
  h2 { font-size: 17px; margin: 4px 0 6px; }
  p { color: #727689; font-size: 12px; margin: 0; }
  ol { display: grid; grid-template-columns: repeat(4, 1fr); gap: 12px; list-style: none; margin: 0; padding: 0; }
  li { display: flex; gap: 9px; align-items: flex-start; font-size: 11px; color: #777b8e;
    strong { flex: 0 0 25px; height: 25px; display: grid; place-items: center; border-radius: 50%; background: #ed274c; color: white; }
    b { display: block; color: #303448; margin-bottom: 3px; }
  }
  .guide-close { position: absolute; right: 14px; top: 14px; border: 0; background: none; color: #737789; font-size: 11px; font-weight: 700; cursor: pointer; }
}
.workspace { display: grid; grid-template-columns: minmax(310px, 340px) minmax(0, 1fr); height: clamp(620px, calc(100vh - 300px), 800px); min-height: 620px; background: white; border: 1px solid #dfe2e9; border-radius: 14px; overflow: hidden; box-shadow: 0 10px 30px #25283a0a; }
.ticket-panel { min-width: 0; border-right: 1px solid #e3e6ed; display: flex; flex-direction: column; background: #fcfcfd; overflow: hidden; }
.ticket-toolbar { flex: 0 0 auto; padding: 14px 14px 10px; background: #fff; border-bottom: 1px solid #eceef3; }
.ticket-list { flex: 1 1 auto; min-height: 0; overflow-y: auto; overscroll-behavior: contain; }
.search-box { min-width: 0; border: 1px solid #dfe1e8; border-radius: 9px; display: flex; align-items: center; gap: 9px; padding: 0 11px; color: #9396a6; background: white;
  &:focus-within { border-color: #eb7289; box-shadow: 0 0 0 3px #ed274c12; }
  input { min-width: 0; width: 100%; border: 0; outline: 0; padding: 10px 0; background: transparent; color: #303448; }
}
.filters { display: flex; gap: 6px; padding-top: 10px; overflow-x: auto;
  button { white-space: nowrap; border: 1px solid #e0e2e9; border-radius: 999px; background: white; color: #717587; padding: 5px 9px; font-size: 10px; font-weight: 700; cursor: pointer;
    &:hover { border-color: #c7cad4; color: #3f4355; }
    &.active { border-color: #ed274c; background: #fff2f4; color: #c92040; }
  }
}
.ticket-card { width: 100%; text-align: left; border: 0; border-bottom: 1px solid #eceef3; background: white; padding: 14px 16px; cursor: pointer; transition: background .16s, box-shadow .16s;
  &:hover { background: #fafafd; }
  &.selected { background: #fff4f6; box-shadow: inset 3px 0 #ed274c; }
  &:focus-visible { position: relative; outline: 3px solid #ed274c35; outline-offset: -3px; }
  strong { display: block; margin: 7px 0 4px; color: #25283a; font-size: 12px; line-height: 1.35; }
  p { color: #6e7285; font-size: 12px; margin: 0; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
}
.ticket-line, .ticket-meta { display: flex; justify-content: space-between; gap: 10px; align-items: center; font-size: 10px; color: #8c90a1; }
.ticket-meta { margin-top: 10px; justify-content: flex-start; min-width: 0; }
.project-name { min-width: 0; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
.status { display: inline-flex; flex: 0 0 auto; width: fit-content; border-radius: 999px; padding: 4px 8px; background: #eef0f6; color: #5f6478; font-size: 9px; font-weight: 800;
  &.needs_guidance, &.failed, &.rejected { background: #fff0e7; color: #a94d18; }
  &.awaiting_execution_approval, &.awaiting_publication_approval, &.ready_for_execution, &.awaiting_approval { background: #fff5d8; color: #876000; }
  &.published, &.approved { background: #e7f8ee; color: #237c49; }
  &.running, &.queued, &.queued_for_analysis, &.analyzing, &.queued_for_implementation, &.queued_for_publication { background: #e9efff; color: #345eaf; }
}
.detail-panel { min-width: 0; padding: 24px; overflow-y: auto; overscroll-behavior: contain; display: flex; flex-direction: column; scroll-behavior: smooth; }
.detail-loading { place-content: center; align-items: center; text-align: center; color: #666b7e;
  .pulse { width: 11px; height: 11px; margin: 0 0 12px; }
  strong { color: #303448; }
  p { margin: 5px 0 0; font-size: 12px; }
}
.detail-header { display: flex; justify-content: space-between; gap: 24px; border-bottom: 1px solid #eceef3; padding-bottom: 18px;
  > div { min-width: 0; }
  h2 { font-size: 21px; line-height: 1.25; margin: 5px 0; overflow-wrap: anywhere; }
  p { margin: 0; color: #7b7f91; font-size: 12px; line-height: 1.45; }
}
.project-select { flex: 0 0 230px;
  span { display: block; font-size: 10px; color: #7c8091; margin-bottom: 5px; }
  select { width: 100%; border: 1px solid #dfe1e8; border-radius: 8px; padding: 9px; background: white; color: #303448;
    &:focus { border-color: #eb7289; outline: 3px solid #ed274c12; }
    &:disabled { background: #f5f6f9; color: #767a8c; }
  }
}
.next-step { order: 1; display: flex; justify-content: space-between; align-items: center; gap: 20px; margin: 16px 0; padding: 14px 16px; border-radius: 10px; border: 1px solid #e0e3eb; background: #f8f9fc;
  span { color: #85899b; font-size: 9px; font-weight: 800; letter-spacing: .04em; text-transform: uppercase; }
  h3 { font-size: 14px; margin: 3px 0; }
  p { font-size: 11px; line-height: 1.45; color: #6d7184; margin: 0; }
  &.attention { border-color: #f2d897; background: #fffaf0; }
  &.processing { border-color: #cdd9f7; background: #f4f7ff; }
  &.success { border-color: #bee5ce; background: #f2fbf6; }
  &.error { border-color: #efc3ca; background: #fff4f5; }
}
.resolution-summary { order: 2; border: 1px solid #cce8d7; background: #f5fcf8; border-radius: 11px; padding: 17px; margin-bottom: 16px; }
.resolution-heading, .section-heading { display: flex; justify-content: space-between; align-items: flex-start; gap: 15px;
  h3 { margin: 3px 0 0; font-size: 15px; }
  p { margin: 4px 0 0; color: #777b8e; font-size: 11px; }
}
.resolution-grid { display: grid; grid-template-columns: 1fr 1fr; gap: 9px; margin-top: 14px;
  div { border-radius: 8px; background: white; padding: 10px; }
  span { font-size: 9px; text-transform: uppercase; color: #7c8091; font-weight: 800; }
  p { margin: 5px 0 0; font-size: 11px; line-height: 1.45; }
}
.codex-card { order: 3; border: 1px solid #dfe2eb; border-radius: 12px; padding: 18px; background: #fafafd; }
.codex-title { display: flex; align-items: center; gap: 12px;
  h3 { margin: 2px 0 0; font-size: 16px; }
  > .status { margin-left: auto; }
}
.codex-icon { flex: 0 0 38px; width: 38px; height: 38px; display: grid; place-items: center; border-radius: 10px; background: #222638; color: white; }
.analysis-grid { display: grid; grid-template-columns: minmax(260px, .72fr) minmax(360px, 1.28fr); gap: 12px; margin-top: 17px; align-items: start; }
.analysis-section { min-width: 0; background: white; border: 1px solid #e3e6ed; border-radius: 10px; padding: 15px 16px;
  > span { display: block; padding-bottom: 9px; border-bottom: 1px solid #eef0f4; font-size: 9px; letter-spacing: .05em; text-transform: uppercase; color: #7a7f92; font-weight: 800; }
}
.diagnosis-copy { border-color: #d9deec; box-shadow: 0 3px 12px #29324d08; }
.formatted-copy { color: #414658;
  p { margin: 10px 0 0; font-size: 12px; line-height: 1.62; overflow-wrap: anywhere; }
  p + p { margin-top: 11px; padding-top: 11px; border-top: 1px solid #f0f1f5; }
}
.questions, .plan, .files { margin-top: 14px; padding: 14px 15px; border: 1px solid #e4e7ee; border-radius: 9px; background: white; font-size: 12px;
  ol { padding-left: 20px; margin: 9px 0 0; list-style: decimal; }
  li { margin: 6px 0; line-height: 1.5; }
  code { display: block; margin-top: 6px; white-space: normal; overflow-wrap: anywhere; }
}
.evidence-list { margin-top: 15px; font-size: 12px;
  > strong { display: block; color: #303448; }
  ul { list-style: none; padding: 0; margin: 9px 0 0; display: grid; gap: 8px; }
  li { background: white; border: 1px solid #e2e5ed; border-radius: 9px; padding: 12px 14px; }
  li > div:first-child { display: flex; justify-content: space-between; gap: 12px; align-items: flex-start; }
  code { max-width: 52%; color: #777c8e; font-size: 9px; text-align: right; white-space: normal; overflow-wrap: anywhere; }
  .evidence-copy p { color: #555a6d; font-size: 11px; }
}
.diagnostic-tools { margin-top: 10px; display: flex; flex-wrap: wrap; gap: 8px; align-items: center; color: #64697c; font-size: 10px;
  span { padding: 4px 7px; border-radius: 999px; background: #e9efff; color: #345eaf; font-weight: 800; }
}
.approval-block, .publish-block { margin-top: 16px;
  textarea { width: 100%; min-height: 96px; border: 1px solid #dfe1e8; border-radius: 9px; padding: 11px; resize: vertical; line-height: 1.5;
    &:focus { border-color: #eb7289; outline: 3px solid #ed274c12; }
  }
}
.publish-block > label { display: block; color: #6f7385; font-size: 11px; margin-top: 12px; }
.checks { display: flex; flex-wrap: wrap; gap: 15px; margin: 14px 0; font-size: 11px; }
.actions { display: flex; flex-wrap: wrap; justify-content: flex-end; gap: 9px; margin-top: 12px; }
.working { margin-top: 16px; padding: 12px 14px; border-radius: 9px; background: #f4f7ff; color: #555c70; font-size: 12px;
  small { display: block; margin: 6px 0 0 15px; color: #85899a; }
}
.pulse { width: 8px; height: 8px; display: inline-block; border-radius: 50%; background: #ed274c; margin-right: 7px; animation: pulse 1.2s infinite; }
.error-box { margin-top: 15px; padding: 11px; background: #fff0f1; color: #ac253b; border-radius: 8px; font-size: 11px; }
.workflow-history { order: 4; margin-top: 16px; border: 1px solid #e2e4eb; border-radius: 11px; padding: 16px;
  ol { list-style: none; margin: 15px 0 0; padding: 0; }
  li { position: relative; display: grid; grid-template-columns: 18px 1fr; gap: 10px; padding-bottom: 15px;
    &:not(:last-child)::before { content: ''; position: absolute; left: 5px; top: 13px; bottom: 0; width: 1px; background: #dfe2e9; }
  }
  .timeline-dot { z-index: 1; width: 11px; height: 11px; margin-top: 4px; border-radius: 50%; background: #a8acb9; border: 2px solid white; box-shadow: 0 0 0 1px #cfd2db;
    &.published, &.ready_for_execution, &.needs_guidance, &.awaiting_approval { background: #42a26b; }
    &.failed { background: #d05a6d; }
    &.running, &.queued { background: #4b72c2; }
  }
  .timeline-content > div { display: flex; flex-wrap: wrap; gap: 8px; align-items: center; }
  time { margin-left: auto; color: #9296a6; font-size: 9px; }
  p { margin: 6px 0 0; color: #65697c; font-size: 11px; line-height: 1.5; }
  small { display: block; margin-top: 5px; color: #7c8091; }
  .timeline-error { color: #ad2940; }
}
.conversation { order: 5; margin-top: 16px; border: 1px solid #e2e4eb; border-radius: 11px; background: #fff; }
.conversation-heading { display: flex; align-items: center; justify-content: space-between; gap: 18px; padding: 15px 16px;
  h3 { font-size: 14px; margin: 0; }
  p { margin: 4px 0 0; color: #777b8e; font-size: 11px; }
}
.message-list { padding: 0 16px 16px; border-top: 1px solid #eceef3; }
.message { max-width: 78%; margin: 10px 0; padding: 11px 13px; border-radius: 11px; background: #f1f2f6;
  &.outgoing { margin-left: auto; background: #fff0f3; }
  div { display: flex; justify-content: space-between; gap: 15px; font-size: 10px; }
  time { color: #9195a4; }
  p { white-space: pre-line; margin: 7px 0 0; font-size: 12px; line-height: 1.55; overflow-wrap: anywhere; }
}
.empty { color: #898d9d; text-align: center; padding: 40px 20px; font-size: 12px; }
.loading-state { display: flex; justify-content: center; align-items: center; }
.empty-state { color: #898d9d; text-align: center; display: grid; place-content: center;
  svg { font-size: 34px; margin: 0 auto 12px; }
  h2 { margin: 0; color: #4b4f61; }
  p { margin: 7px 0 0; }
}
.knowledge-panel { display: grid; gap: 13px; max-width: 1180px; margin: 0 auto; }
.knowledge-header { display: flex; justify-content: space-between; align-items: flex-end; gap: 24px;
  h2 { margin: 4px 0 5px; }
  p { margin: 0; color: #74788a; }
}
.knowledge-summary { display: grid; grid-template-columns: auto auto; gap: 2px 9px; align-items: baseline; min-width: 170px;
  strong { color: #25283a; font-size: 20px; }
  span { color: #7b7f91; font-size: 11px; }
}
.knowledge-toolbar { display: flex; align-items: center; gap: 10px; background: white; border: 1px solid #e2e4eb; border-radius: 11px; padding: 7px 12px;
  .search-box { flex: 1; }
  .filters { padding: 0; }
}
.knowledge-card { background: white; border: 1px solid #e2e4eb; border-radius: 11px; padding: 18px;
  .knowledge-meta { display: flex; flex-wrap: wrap; gap: 10px; align-items: center; }
  h3 { margin: 12px 0 8px; }
  p { color: #626678; white-space: pre-line; line-height: 1.55; }
  footer { display: flex; justify-content: space-between; align-items: center; gap: 12px; margin-top: 14px; }
}
.tags { display: flex; flex-wrap: wrap; gap: 6px;
  span { border-radius: 999px; padding: 4px 8px; background: #f0f1f5; color: #6b6f80; font-size: 10px; }
}
.text-button { border: 0; background: none; padding: 0; color: #c92040; font-weight: 700; cursor: pointer;
  &:hover { text-decoration: underline; }
}
.knowledge-empty { background: white; border: 1px dashed #d8dbe4; border-radius: 12px;
  h3 { color: #45495c; }
  p { max-width: 600px; margin: 8px auto 16px; }
}
.toast { position: fixed; right: 25px; bottom: 25px; background: #24392e; color: white; padding: 13px 17px; border-radius: 9px; box-shadow: 0 10px 30px #0002; z-index: 2000;
  &.error { background: #a8273c; }
}
@keyframes pulse { 50% { opacity: .25; transform: scale(.7); } }
@media (max-width: 1180px) {
  .quick-guide { grid-template-columns: 1fr; ol { grid-template-columns: 1fr 1fr; } }
  .workspace { grid-template-columns: 310px minmax(0, 1fr); }
}
@media (max-width: 1000px) {
  .workspace { height: auto; min-height: 0; grid-template-columns: 1fr; }
  .ticket-panel { max-height: 360px; border-right: 0; border-bottom: 1px solid #e3e6ed; }
  .workspace.has-detail .ticket-panel { max-height: 260px; }
  .detail-panel { min-height: 540px; max-height: none; }
  .detail-header, .knowledge-header, .knowledge-toolbar { flex-direction: column; align-items: stretch; }
  .project-select { flex-basis: auto; }
}
@media (max-width: 720px) {
  .analysis-grid, .resolution-grid { grid-template-columns: 1fr; }
  .actions { justify-content: stretch; .button { flex: 1 1 100%; } }
  .evidence-list { li > div:first-child { flex-direction: column; } code { max-width: 100%; text-align: left; } }
}
@media (max-width: 620px) {
  .copilot-apollo-header .apollo-header-description { display: none; }
  .copilot-page { padding: 16px; }
  .stats { grid-template-columns: 1fr 1fr; gap: 8px; button { min-height: 74px; padding: 12px; } }
  .tabs { gap: 16px; overflow-x: auto; }
  .quick-guide ol { grid-template-columns: 1fr; }
  .detail-panel { min-height: 480px; padding: 18px; }
  .detail-header { flex-direction: column; }
  .next-step, .conversation-heading { align-items: flex-start; flex-direction: column; }
  .codex-title { align-items: flex-start; flex-wrap: wrap; > .status { margin-left: 50px; } }
  .message { max-width: 92%; }
  .knowledge-card footer { align-items: stretch; flex-direction: column; }
}
</style>
